import React from 'react';

/**
 * Premium SVG illustrations — geometric, minimal, on-brand acid + dark.
 * Style rules: no gradients that feel "3d", no cartoons, thin strokes, luminous accents.
 * Each illustration renders at intrinsic 200×160; parent controls scaling.
 */

const ACC = '#D4FF00';

// --- Dashboard empty — "start creating" hint (rising bars + spark).
export const DashboardIllustration = ({ className = 'w-40 h-32' }) => (
  <svg viewBox="0 0 200 160" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="dash-glow" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={ACC} stopOpacity="0.55" />
        <stop offset="100%" stopColor={ACC} stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* baseline */}
    <line x1="20" y1="120" x2="180" y2="120" stroke="#ffffff20" strokeDasharray="2 3" />
    {/* rising bars */}
    {[
      { x: 32, h: 20, o: 0.35 },
      { x: 58, h: 40, o: 0.55 },
      { x: 84, h: 66, o: 0.85 },
      { x: 110, h: 88, o: 1 },
    ].map((b, i) => (
      <g key={i}>
        <rect x={b.x} y={120 - b.h} width="16" height={b.h} rx="3" fill={ACC} opacity={b.o} />
        <rect x={b.x} y={120 - b.h} width="16" height={b.h} rx="3" fill="url(#dash-glow)" opacity="0.35" />
      </g>
    ))}
    {/* Spark on top of the tallest */}
    <g transform="translate(148, 22)">
      <path d="M0 -10 L3 -3 L10 0 L3 3 L0 10 L-3 3 L-10 0 L-3 -3 Z" fill={ACC} opacity="0.9" />
    </g>
    {/* Curve suggesting growth */}
    <path d="M 20 110 Q 60 100 100 70 T 180 30"
          fill="none" stroke="#00E5FF" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.55" />
  </svg>
);

// --- AI Memory empty — pentagon (5-axis DNA) skeleton with one filled vertex.
export const MemoryIllustration = ({ className = 'w-40 h-32' }) => {
  const cx = 100, cy = 78, R = 52, N = 5;
  const points = Array.from({ length: N }).map((_, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / N;
    return { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
  });
  const inner = points.map((p) => {
    const dx = p.x - cx, dy = p.y - cy;
    return { x: cx + dx * 0.25, y: cy + dy * 0.25 };
  });
  return (
    <svg viewBox="0 0 200 160" className={className} aria-hidden="true">
      <polygon
        points={points.map((p) => `${p.x},${p.y}`).join(' ')}
        fill="none" stroke="#ffffff18"
      />
      <polygon
        points={inner.map((p) => `${p.x},${p.y}`).join(' ')}
        fill={`${ACC}22`} stroke={ACC} strokeWidth="1.5"
      />
      {points.map((p, i) => (
        <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#ffffff10" />
      ))}
      {points.map((p, i) => (
        <circle key={`c${i}`} cx={p.x} cy={p.y} r={2.5} fill="#ffffff30" />
      ))}
      {/* one lit vertex */}
      <circle cx={points[0].x} cy={points[0].y} r={4} fill={ACC} />
      {/* Bottom label ticks representing "sparse data" */}
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={`t${i}`} x={44 + i * 24} y={142} width={i === 0 ? 16 : 10} height="3" rx="1.5"
              fill={i === 0 ? ACC : '#ffffff15'} />
      ))}
    </svg>
  );
};

// --- Growth empty — arrow-up on a chart card.
export const GrowthIllustration = ({ className = 'w-40 h-32' }) => (
  <svg viewBox="0 0 200 160" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="grw-area" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={ACC} stopOpacity="0.35" />
        <stop offset="100%" stopColor={ACC} stopOpacity="0" />
      </linearGradient>
    </defs>
    <rect x="16" y="24" width="168" height="120" rx="10" fill="#ffffff05" stroke="#ffffff15" />
    {/* grid */}
    {[0, 1, 2].map((i) => (
      <line key={i} x1="24" x2="176" y1={54 + i * 28} y2={54 + i * 28} stroke="#ffffff10" strokeDasharray="2 3" />
    ))}
    {/* area */}
    <path d="M 26 118 L 55 108 L 82 92 L 108 74 L 136 58 L 168 38 L 168 130 L 26 130 Z" fill="url(#grw-area)" />
    <path d="M 26 118 L 55 108 L 82 92 L 108 74 L 136 58 L 168 38"
          fill="none" stroke={ACC} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    {/* end point + arrow */}
    <circle cx="168" cy="38" r="3.5" fill={ACC} />
    <g transform="translate(168 38)">
      <path d="M 0 -14 L -6 -6 L -2 -6 L -2 4 L 2 4 L 2 -6 L 6 -6 Z" fill={ACC} opacity="0.75" />
    </g>
    {/* small ticks along x-axis */}
    {[0, 1, 2, 3].map((i) => (
      <rect key={`x${i}`} x={40 + i * 40} y="138" width="6" height="2" rx="1" fill="#ffffff20" />
    ))}
  </svg>
);

// --- Publishing empty — 3 platform badges around a central plug.
export const PublishingIllustration = ({ className = 'w-40 h-32' }) => (
  <svg viewBox="0 0 200 160" className={className} aria-hidden="true">
    <circle cx="100" cy="80" r="24" fill={`${ACC}18`} stroke={ACC} strokeWidth="1.5" />
    <path d="M 92 74 L 108 80 L 92 86 Z" fill={ACC} />
    {[
      { cx: 45, cy: 45, label: 'TT' },
      { cx: 155, cy: 45, label: 'YT' },
      { cx: 45, cy: 118, label: 'IG' },
      { cx: 155, cy: 118, label: 'LI' },
    ].map((p, i) => (
      <g key={i}>
        <line x1={p.cx} y1={p.cy} x2="100" y2="80" stroke="#ffffff10" strokeDasharray="3 3" />
        <circle cx={p.cx} cy={p.cy} r={16} fill="#ffffff08" stroke="#ffffff20" />
        <text x={p.cx} y={p.cy + 4} textAnchor="middle" fontSize="10" fontFamily="JetBrains Mono, monospace" fill="#71717a">
          {p.label}
        </text>
      </g>
    ))}
  </svg>
);
